import { parentAttributeListPath } from "@dashboard/parentAttributes/urls";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import CardSpacer from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import Form from "@dashboard/components/Form";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { DashboardCard } from "@dashboard/components/Card";
import { useBackLinkWithState } from "@dashboard/hooks/useBackLinkWithState";
import useNavigator from "@dashboard/hooks/useNavigator";
// import { sectionNames } from "@dashboard/intl";
import { TextField } from "@material-ui/core";
import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl, FormattedMessage } from "react-intl";
import slugify from "slugify";

export interface ParentAttributePageFormData {
  name: string;
  slug: string;
}

export interface ParentAttributePageProps {
  parentAttribute?: {
    id: string;
    name: string;
    slug: string;
    children?: Array<{ id: string; name: string; slug: string }>;
  } | null;
  disabled: boolean;
  errors: Array<{ field?: string | null; message?: string | null }>;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: ParentAttributePageFormData) => Promise<{ id?: string; errors: any[] }>;
  onDelete?: () => void;
}

const ParentAttributePage = ({
  parentAttribute,
  disabled,
  errors: apiErrors,
  saveButtonBarState,
  onSubmit,
  onDelete,
}: ParentAttributePageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();

  const initialForm: ParentAttributePageFormData = {
    name: parentAttribute?.name ?? "",
    slug: parentAttribute?.slug ?? "",
  };

  const handleSubmit = (data: ParentAttributePageFormData) => {
    return onSubmit({
      ...data,
      slug: data.slug || slugify(data.name).toLowerCase(),
    });
  };

  const parentAttributePageBackLink = useBackLinkWithState({
    path: parentAttributeListPath,
  });

  const isCreate = !parentAttribute;

  return (
    <Form confirmLeave initial={initialForm} onSubmit={handleSubmit} disabled={disabled}>
      {({ change, data, isSaveDisabled, submit }) => {
        return (
          <DetailPageLayout>
            <TopNav
              href={parentAttributePageBackLink}
              title={
                isCreate
                  ? intl.formatMessage({
                      id: "createParentAttrTitle",
                      defaultMessage: "Create Parent Attribute",
                      description: "page title",
                    })
                  : parentAttribute?.name
              }
            />
            <DetailPageLayout.Content>
              <DashboardCard>
                <DashboardCard.Header>
                  <DashboardCard.Title>
                    <FormattedMessage
                      id="parentAttrGeneralInfo"
                      defaultMessage="General Information"
                    />
                  </DashboardCard.Title>
                </DashboardCard.Header>
                <DashboardCard.Content>
                  <TextField
                    disabled={disabled}
                    error={!!apiErrors.find(e => e.field === "name")}
                    fullWidth
                    helperText={apiErrors.find(e => e.field === "name")?.message}
                    label={intl.formatMessage({
                      id: "parentAttrNameLabel",
                      defaultMessage: "Name",
                    })}
                    name="name"
                    value={data.name}
                    onChange={change}
                  />
                  <Box marginTop={4}>
                    <TextField
                      disabled={disabled}
                      error={!!apiErrors.find(e => e.field === "slug")}
                      fullWidth
                      helperText={
                        apiErrors.find(e => e.field === "slug")?.message ||
                        intl.formatMessage({
                          id: "parentAttrSlugHelp",
                          defaultMessage: "This is used internally. Make sure you don’t use spaces",
                        })
                      }
                      label={intl.formatMessage({
                        id: "parentAttrSlugLabel",
                        defaultMessage: "Slug",
                      })}
                      name="slug"
                      value={data.slug}
                      onChange={change}
                    />
                  </Box>
                </DashboardCard.Content>
              </DashboardCard>

              {!isCreate && parentAttribute?.children && parentAttribute.children.length > 0 && (
                <>
                  <CardSpacer />
                  <DashboardCard>
                    <DashboardCard.Header>
                      <DashboardCard.Title>
                        <FormattedMessage
                          id="parentAttrChildrenTitle"
                          defaultMessage="Child Attributes"
                        />
                      </DashboardCard.Title>
                    </DashboardCard.Header>
                    <DashboardCard.Content>
                      <Box display="flex" flexDirection="column" gap={2}>
                        {parentAttribute.children.map(child => (
                          <Box
                            key={child.id}
                            padding={3}
                            borderRadius={2}
                            borderStyle="solid"
                            borderWidth={1}
                            borderColor="default1"
                          >
                            <Text fontWeight="medium">{child.name}</Text>
                            <Text fontWeight="medium" marginLeft={1}>
                              {`(${child.slug})`}
                            </Text>
                          </Box>
                        ))}
                      </Box>
                    </DashboardCard.Content>
                  </DashboardCard>
                </>
              )}
            </DetailPageLayout.Content>
            <Savebar>
              {!isCreate && onDelete && <Savebar.DeleteButton onClick={onDelete} />}
              <Savebar.Spacer />
              <Savebar.CancelButton onClick={() => navigate(parentAttributePageBackLink)} />
              <Savebar.ConfirmButton
                transitionState={saveButtonBarState}
                onClick={submit}
                disabled={!!isSaveDisabled}
              />
            </Savebar>
          </DetailPageLayout>
        );
      }}
    </Form>
  );
};

ParentAttributePage.displayName = "ParentAttributePage";
export default ParentAttributePage;